import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { ProjectRole } from '../types/api';

export interface ProjectMember {
  project_id: string;
  user_id: string;
  role: ProjectRole;
  invited_by: string | null;
  joined_at: string;
  created_at: string;
  updated_at: string;
  username: string;
  email: string;
}

export const projectMembersKeys = {
  all: ['project-members'] as const,
  byProject: (id: string) => [...projectMembersKeys.all, 'byProject', id] as const,
};

export function useProjectMembers(projectId: string | null) {
  return useQuery({
    queryKey: projectId ? projectMembersKeys.byProject(projectId) : ['project-members', 'none'],
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ members: ProjectMember[] }>(`/projects/${projectId}/members`);
      return res.members;
    },
  });
}

export function useAddProjectMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { user_id: string; role: 'moderator' | 'editor' | 'viewer' }) =>
      api.post<{ member: ProjectMember }>(`/projects/${projectId}/members`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectMembersKeys.byProject(projectId) }),
  });
}

export function useUpdateMemberRole(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectRole }) =>
      api.patch<{ member: ProjectMember }>(`/projects/${projectId}/members/${userId}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectMembersKeys.byProject(projectId) }),
  });
}

export function useRemoveMember(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/projects/${projectId}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectMembersKeys.byProject(projectId) }),
  });
}

export function useTransferOwnership(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { new_owner_id: string }) =>
      api.post<{ project: unknown; members: ProjectMember[] }>(
        `/projects/${projectId}/transfer-ownership`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectMembersKeys.byProject(projectId) });
      // The project owner_id changed, so the broader project listings need to refetch.
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['admin-projects'] });
    },
  });
}
